import React, { useMemo } from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import { useHistory, useParams } from "react-router";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useTranslation } from "react-i18next";
import { useConfig } from "../../contexts/ConfigContext";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import {
  useIonRouter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { useAuth } from "../../contexts/AuthContext";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import Info from "../../components/atoms/ErrorComponent";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";
import {
  TITLE_MAP,
  SEQUENCE_MAP,
  INLINE_SEQUENCE_MAP,
  ALL_ISLANDS,
  WeatherKey,
  QuestionnaireCardConfig,
} from "./IslandRegistry";
import { useLatestScores, useTodayScores } from "../../hooks/useScoredToday";
import InlineQuestion from "../../components/organisms/InlineQuestion/InlineQuestion";
import {
  getApiFullSequence,
  FullSequenceResponse,
} from "../../services/questionnaire.service";
import { useFetch } from "../../hooks/useFetch";
import {
  defaultAnswerValueByType,
  formatAnswers,
  getAnswerValue,
} from "../../helper/answer";
import { useIslandVisibility } from "../../components/organisms/ResonanceContext/IslandVisibilityContext";
import ExplicationIcon from "../../assets/MentalPlus/Icone-question.svg";
import SolutionIcon from "../../assets/MentalPlus/Icone-light.svg";
import IslandQuestionnaireCard from "./IslandQuestionnaireCard";
import { cardSurface } from "../../components/atoms/Card";

const formatNextAvailabilityText = (nextEligibleAt: string | null | undefined) => {
  if (!nextEligibleAt) {
    return "Le questionnaire réapparaîtra automatiquement lorsqu'une nouvelle passation sera ouverte.";
  }

  const nextAvailabilityDate = dayjs(nextEligibleAt).startOf("day");
  const today = dayjs().startOf("day");
  const daysUntilAvailable = nextAvailabilityDate.diff(today, "day");
  const formattedDate = nextAvailabilityDate.format("DD/MM/YYYY");

  if (daysUntilAvailable <= 0) {
    return "Le questionnaire sera disponible très bientôt.";
  }

  if (daysUntilAvailable === 1) {
    return `Prochain questionnaire demain (${formattedDate}).`;
  }

  return `Prochain questionnaire dans ${daysUntilAvailable} jours (${formattedDate}).`;
};

const DisplayIsland: React.FC = () => {
  const {
    sequences,
    hasFetchedSequences,
    loading,
    error,
    fullSequenceByUserSequenceId,
    sequenceAvailabilityBySequenceId,
  } = useResonanceData();

  const history = useHistory();
  const { study_slug, title: key } = useParams<{
    study_slug: string;
    title: string;
  }>();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { study } = useConfig();
  const { user } = useAuth();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();
  const { hasScoreToday, todayScores } = useTodayScores();
  const { latestScores } = useLatestScores();
  const { getTitle } = useIslandVisibility();

  const displayTitle = (key ? getTitle(key) : "") || (key ? TITLE_MAP[key] : "") || key || "";

  useIonViewWillEnter(() => {
    if (!key) return;

    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: displayTitle,
      welcomePage: false,
      headerLeftContent: "back",
      headerBackFallbackPath: `/${study_slug}/tabs/home`,
    });
  });

  useIonViewWillLeave(() => {
    setResonanceLayout({
      headerLeftContent: "logo",
      headerBackFallbackPath: "",
    });
  });

  const inlineSeqId = key ? INLINE_SEQUENCE_MAP[key] : undefined;
  const inlineSeq = sequences?.find((s) => s.sequence_id === inlineSeqId);
  const hasInlineData = !!(inlineSeq && inlineSeq.components?.length);

  const userSequenceId = inlineSeq?.id ?? 0;
  const questionnaireId = inlineSeq?.components?.[0]?.questionnaire_id ?? 0;
  const componentId = inlineSeq?.components?.[0]?.id ?? 0;

  const cached = userSequenceId
    ? fullSequenceByUserSequenceId?.[userSequenceId]
    : undefined;

  const fetchEnabled = Boolean(
    !loading && inlineSeqId && hasInlineData && userSequenceId && !cached
  );

  const { data: fetched, isLoading, isError } = useFetch<FullSequenceResponse>(
    `inline-sequence:${userSequenceId}`,
    getApiFullSequence,
    {
      studyId: study.id,
      userId: user.id,
      sequenceId: userSequenceId,
      lang: language,
    },
    { enabled: fetchEnabled }
  );

  const data = cached ?? fetched;

  const inlineQuestionnaire = useMemo(() => {
    if (!data?.questionnaires?.length || !questionnaireId) return undefined;

    return data.questionnaires.find(
      (q) => q.id.toString() === questionnaireId.toString()
    );
  }, [data?.questionnaires, questionnaireId]);

  const firstQuestion = inlineQuestionnaire?.questions?.[0];

  const initialValue = useMemo(() => {
    if (!firstQuestion) return null;

    const formatted = formatAnswers(data?.answers?.answers ?? []);
    const previousAnswer = formatted.find(
      (a) => a.question_id === firstQuestion.id
    );

    if (previousAnswer) {
      return getAnswerValue(firstQuestion.type, previousAnswer);
    }

    return defaultAnswerValueByType[firstQuestion.type];
  }, [data?.answers?.answers, firstQuestion]);

  const def = key ? ALL_ISLANDS[key] : undefined;
  const mainSequenceId = def?.main.sequenceId;
  const mainSeq = sequences?.find((s) => s.sequence_id === mainSequenceId);
  const nextMainComponent = mainSeq?.components.find((c) => !c.is_done);
  const canOpenQuestionnaire = !!(mainSeq && nextMainComponent);
  const mainAvailability =
    mainSequenceId != null ? sequenceAvailabilityBySequenceId[mainSequenceId] : undefined;
  const isQuestionnaireAvailable = mainAvailability
    ? mainAvailability.isAvailableNow && canOpenQuestionnaire
    : canOpenQuestionnaire;
  const iconSourceCfg = def
    ? def.iconFromInline
      ? def.inline ?? def.main
      : def.main
    : undefined;
  const iconSequenceId = iconSourceCfg?.sequenceId;

  const hasScore = !!iconSequenceId && hasScoreToday.has(iconSequenceId);
  const todaysScore =
    iconSequenceId != null ? todayScores.get(iconSequenceId) : undefined;
  const questionnaireCardConfig: QuestionnaireCardConfig =
    def?.questionnaireCard ?? {
      launchTitle: `Questionnaire ${displayTitle}`,
      resultTitle: "Résultat du questionnaire",
      resultPolicy: "none",
      unavailableMessage: "next-availability",
      launchAction: { kind: "questionnaire", label: "Commencer" },
      unavailableAction: { kind: "evolution", label: "Evolution", icon: "graph" },
      resultAction: { kind: "none" },
    };
  const todayMainScore =
    mainSequenceId != null ? todayScores.get(mainSequenceId) : undefined;
  const latestMainScore =
    mainSequenceId != null ? latestScores.get(mainSequenceId) : undefined;
  const nextAvailabilityText = formatNextAvailabilityText(
    mainAvailability?.nextEligibleAt
  );

  const iconScale =
    iconSourceCfg?.islandScale?.mode === "sprite"
      ? iconSourceCfg.islandScale
      : undefined;

  let Icon = def?.NightComponent;

  if (def?.weatherSprites) {
    if (!hasScore) {
      Icon = def.weatherSprites.nuit ?? def.NightComponent;
    } else if (todaysScore != null && iconScale) {
      const spriteKey = iconScale.map(todaysScore) as WeatherKey;
      Icon = def.weatherSprites[spriteKey] ?? def.NightComponent;
    } else {
      Icon = def.weatherSprites.soleil ?? def.NightComponent;
    }
  }

  const resultScore =
    questionnaireCardConfig.resultPolicy === "same-day"
      ? todayMainScore
      : questionnaireCardConfig.resultPolicy === "latest-while-locked"
        ? latestMainScore
        : undefined;

  if (loading || !hasFetchedSequences) return <ResonanceLoader />;

  if (error || !sequences?.length) {
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
        onClick={() => ionRouter.canGoBack() && ionRouter.goBack()}
      />
    );
  }

  if (!key) {
    return (
      <Info
        title={t("common_nodata_title")}
        description="Aucune île demandée."
        actionText={t("common_go_back")}
        onClick={() => ionRouter.canGoBack() && ionRouter.goBack()}
      />
    );
  }

  if (!inlineSeqId) {
    return (
      <Info
        title={t("common_nodata_title")}
        description={`Aucun questionnaire associé à ${key}.`}
        actionText=""
      />
    );
  }

  if (!hasInlineData) {
    return <ResonanceLoader />;
  }

  if (!def || !Icon) {
    return (
      <Info
        title={t("common_nodata_title")}
        description={`Aucune configuration trouvée pour ${key}.`}
        actionText=""
      />
    );
  }

  const shouldShowQuizCard = isQuestionnaireAvailable;
  const shouldShowResultCard =
    !isQuestionnaireAvailable &&
    questionnaireCardConfig.resultPolicy !== "none" &&
    resultScore != null;
  const shouldShowUnavailableCard = !isQuestionnaireAvailable && !shouldShowResultCard;

  const goToVideo = (contentType: "Explications" | "Solutions") =>
    history.push(`/${study_slug}/tabs/home/island/${key}/${contentType}`);

  const goToEvolution = () => {
    history.push(`/${study_slug}/tabs/home/island/${key}/evolution`);
  };

  const goToQuestionnaire = () => {
    const seqId = SEQUENCE_MAP[key];
    if (!seqId) return;

    const seq = sequences.find((s) => s.sequence_id === seqId);
    if (!seq) return;

    const nextComponent = seq.components.find((c) => !c.is_done);
    if (!nextComponent) return;

    history.push(
      `/${study_slug}/tabs/home/island/${key}/sequences/${seq.id}/components/${nextComponent.id}`
    );

    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: t("questionnaire_title"),
      welcomePage: false,
    });
  };

  return (
    <ResonanceLayout isConnected>
      <PageWrapper>
        {isLoading && !cached && <ResonanceLoader />}

        {isError && (
          <Info
            title={t("common_server_error_title")}
            description={t("common_server_error_desc")}
            actionText=""
          />
        )}

        {firstQuestion && (
          <TopCardWrapper>
            <InlineQuestion
              question={firstQuestion}
              questionnaireId={questionnaireId}
              userSequenceId={userSequenceId}
              componentId={componentId}
              questionnaireData={inlineQuestionnaire}
              initialValue={initialValue}
              autoSubmit={true}
              cardMargin="0px"
            />
          </TopCardWrapper>
        )}

        <ButtonsColumn>
          {shouldShowQuizCard && (
            <IslandQuestionnaireCard
              mode="launch"
              config={questionnaireCardConfig}
              onQuestionnaire={goToQuestionnaire}
            />
          )}

          {shouldShowResultCard && resultScore != null && (
            <IslandQuestionnaireCard
              mode="result"
              config={questionnaireCardConfig}
              score={resultScore}
              nextAvailabilityText={nextAvailabilityText}
              onEvolution={goToEvolution}
            />
          )}

          {shouldShowUnavailableCard && (
            <IslandQuestionnaireCard
              mode="unavailable"
              config={questionnaireCardConfig}
              nextAvailabilityText={nextAvailabilityText}
              onEvolution={goToEvolution}
            />
          )}

          <SmallCardsGroup>
            <ActionCard onClick={() => goToVideo("Explications")} type="button">
              <LeftIcon>
                <CardIcon src={ExplicationIcon} alt="explication" />
              </LeftIcon>
              <CardCenterText>Explications</CardCenterText>
              <RightArrow>›</RightArrow>
            </ActionCard>

            <ActionCard onClick={() => goToVideo("Solutions")} type="button">
              <LeftIcon>
                <CardIcon src={SolutionIcon} alt="solution" />
              </LeftIcon>
              <CardCenterText>Que faire ?</CardCenterText>
              <RightArrow>›</RightArrow>
            </ActionCard>
          </SmallCardsGroup>
        </ButtonsColumn>
      </PageWrapper>
    </ResonanceLayout>
  );
};

export default DisplayIsland;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  padding-top: 10px;
  padding-bottom: 10px;
  gap: 18px;
`;

const TopCardWrapper = styled.div`
  width: 100%;
  max-width: 340px;
  margin: 0 auto;
`;

const CardIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const ButtonsColumn = styled.div`
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const ActionCard = styled.button<{ large?: boolean }>`
  width: 100%;
  min-height: ${({ large }) => (large ? "88px" : "68px")};
  background: #ffffff;
  ${cardSurface}
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  position: relative;

  &:active {
    transform: scale(0.98);
  }
`;

const LeftIcon = styled.div`
  width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
`;

const CardCenterText = styled.div<{ large?: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${({ large }) => (large ? "1.05rem" : "0.95rem")};
  font-weight: 700;
  color: #111;
  text-align: center;
`;

const RightArrow = styled.div`
  font-size: 3.2rem;
  line-height: 1;
  color: #4285f4;
  flex-shrink: 0;
  z-index: 1;
  transform: translateY(-2px);
`;

const SmallCardsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 12px;
`;
